import { useState, useRef, useCallback } from 'react'
import { Upload, File, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import './FileUploadDropzone.css'

function FileUploadDropzone({ config, onUploadComplete, onFileStaged }) {
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)

  const acceptedTypes = config?.accept || '.zip,.tar,.gz'

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files)
    addFiles(droppedFiles)
  }, [])

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files)
    addFiles(selectedFiles)
  }

  const addFiles = (newFiles) => {
    const fileList = newFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      name: file.name,
      size: file.size,
      status: 'ready', // ready, uploading, complete, error
      progress: 0,
    }))
    setFiles(prev => {
      const updated = [...prev, ...fileList]
      // Schedule parent notification after render completes
      setTimeout(() => {
        if (onFileStaged) {
          onFileStaged(updated)
        }
      }, 0)
      return updated
    })
  }

  const removeFile = (fileId) => {
    setFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId)
      // Schedule parent notification after render completes
      setTimeout(() => {
        if (onFileStaged) {
          onFileStaged(updated)
        }
      }, 0)
      return updated
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="file-upload-dropzone">
      <div
        className={`dropzone ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <Upload size={32} className="dropzone-icon" />
        <p className="dropzone-text">
          Drag & drop files here, or <span className="browse-link">browse</span>
        </p>
        <p className="dropzone-hint">
          Accepts: {acceptedTypes}
        </p>
      </div>

      {files.length > 0 && (
        <div className="file-list">
          {files.map(fileEntry => (
            <div key={fileEntry.id} className={`file-item ${fileEntry.status}`}>
              <File size={16} className="file-icon" />
              <div className="file-info">
                <span className="file-name">{fileEntry.name}</span>
                <span className="file-size">{formatFileSize(fileEntry.size)}</span>
              </div>
              <div className="file-status">
                {fileEntry.status === 'ready' && (
                  <button className="remove-btn" onClick={(e) => { e.stopPropagation(); removeFile(fileEntry.id) }}>
                    <X size={14} />
                  </button>
                )}
                {fileEntry.status === 'uploading' && (
                  <Loader2 size={16} className="spinning" />
                )}
                {fileEntry.status === 'complete' && (
                  <CheckCircle size={16} className="success-icon" />
                )}
                {fileEntry.status === 'error' && (
                  <AlertCircle size={16} className="error-icon" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <p className="staged-hint">
          <CheckCircle size={12} /> {files.length} file{files.length > 1 ? 's' : ''} ready - will upload when workflow runs
        </p>
      )}
    </div>
  )
}

export default FileUploadDropzone
